import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AiCredentialsSettings,
  type AiCredentialProvider,
  type AnthropicCredentialsUpdate,
  type TypeSafeCredentialsUpdate,
  type VertexCredentialsUpdate,
} from '@rp/contracts'
import { useReadySession } from '../../session/useSession'
import { aiStatusKeys } from '../../shell/aiStatus'
import { settingsKeys } from './queries'

export const AI_CREDENTIALS_PATH = '/v1/ai-credentials'

const useApplySaved = () => {
  const { membership } = useReadySession()
  const queryClient = useQueryClient()
  return (saved: AiCredentialsSettings) => {
    queryClient.setQueryData(settingsKeys.aiCredentials(membership.tenantId), saved)
    void queryClient.invalidateQueries({ queryKey: aiStatusKeys.tenant(membership.tenantId) })
    void queryClient.invalidateQueries({ queryKey: settingsKeys.tenant(membership.tenantId) })
  }
}

export const useAiCredentials = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: settingsKeys.aiCredentials(membership.tenantId),
    queryFn: async () => (await api.request(AiCredentialsSettings, { method: 'GET', path: AI_CREDENTIALS_PATH })).data,
  })
}

export const useSaveVertexCredentials = () => {
  const { api } = useReadySession()
  const onSuccess = useApplySaved()
  return useMutation({
    mutationFn: async (body: VertexCredentialsUpdate) =>
      (await api.request(AiCredentialsSettings, { method: 'PUT', path: `${AI_CREDENTIALS_PATH}/vertex`, body })).data,
    onSuccess,
  })
}

export const useSaveTypeSafeCredentials = () => {
  const { api } = useReadySession()
  const onSuccess = useApplySaved()
  return useMutation({
    mutationFn: async (body: TypeSafeCredentialsUpdate) =>
      (await api.request(AiCredentialsSettings, { method: 'PUT', path: `${AI_CREDENTIALS_PATH}/typesafe`, body })).data,
    onSuccess,
  })
}

export const useSaveAnthropicCredentials = () => {
  const { api } = useReadySession()
  const onSuccess = useApplySaved()
  return useMutation({
    mutationFn: async (body: AnthropicCredentialsUpdate) =>
      (await api.request(AiCredentialsSettings, { method: 'PUT', path: `${AI_CREDENTIALS_PATH}/anthropic`, body })).data,
    onSuccess,
  })
}

export const useRemoveAiCredentials = () => {
  const { api } = useReadySession()
  const onSuccess = useApplySaved()
  return useMutation({
    mutationFn: async (provider: AiCredentialProvider) =>
      (await api.request(AiCredentialsSettings, { method: 'DELETE', path: `${AI_CREDENTIALS_PATH}/${provider}` })).data,
    onSuccess,
  })
}
